import React, { useEffect, useState } from 'react';
import { api } from '../api.js';
import { useLang } from '../i18n.js';
import { thb, relTime } from '../money.js';
import { Spinner, Empty, Avatar, EntityGlyph, Tier, Segmented, entityColor } from '../ui.jsx';

/**
 * Leads directory.
 *
 * One table over the whole `companies` spine, filtered by lifecycle — the same
 * screen becomes Accounts once servicing lands. The Coverage filter is here
 * from the start because unowned accounts are the gap the design built a whole
 * tab around, and v2 shipped with 79 of 119 companies missing an AE.
 */
export default function Clients({ me, onOpenClient }) {
  const { t, lang } = useLang();
  const [lifecycle, setLifecycle] = useState('lead');
  const [gaps, setGaps] = useState(false);
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setData(null);
    const id = setTimeout(() => {
      api
        .companies({ lifecycle, q, ...(gaps ? { unassigned: 'any' } : {}) })
        .then(setData)
        .catch((e) => setError(e.message));
    }, q ? 250 : 0); // debounce typing, but don't delay tab switches
    return () => clearTimeout(id);
  }, [lifecycle, q, gaps]);

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          padding: '18px 24px 10px',
        }}
      >
        <div className="display" style={{ fontSize: 22, marginRight: 'auto' }}>
          {t('navClients')}
        </div>
        <Segmented
          value={lifecycle}
          onChange={setLifecycle}
          options={[
            { value: 'lead', label: t('navClients') },
            { value: 'account', label: t('navAccounts') },
          ]}
        />
        <button className={`chip${gaps ? ' is-on' : ''}`} onClick={() => setGaps((v) => !v)}>
          Coverage gaps
        </button>
        <input
          className="input"
          style={{ width: 200, height: 30 }}
          placeholder={t('search')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {error && <div className="page">{error}</div>}
      {!error && !data && <Spinner label={t('loading')} />}

      {data && (
        <div style={{ padding: '0 24px 24px' }}>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>{t('industry')}</th>
                  <th>{t('contact')}</th>
                  <th>Sales</th>
                  <th>AE</th>
                  <th style={{ textAlign: 'right' }}>{t('openDeals')}</th>
                  <th style={{ textAlign: 'right' }}>{t('revenueYtd')}</th>
                  <th>{t('lastActivity')}</th>
                </tr>
              </thead>
              <tbody>
                {data.companies.map((c) => (
                  <tr key={c.id} onClick={() => onOpenClient(c.id)}>
                    <td style={{ whiteSpace: 'normal', minWidth: 220 }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 7,
                          fontWeight: 600,
                        }}
                      >
                        <EntityGlyph entity={c.entity_tag} />
                        {c.name}
                        <Tier tier={c.tier} />
                      </span>
                      {c.name_registered_th && (
                        <div lang="th" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                          {c.name_registered_th}
                        </div>
                      )}
                    </td>
                    <td>{c.industry || '—'}</td>
                    <td>
                      {c.contact_name ? (
                        <>
                          <div>{c.contact_name}</div>
                          <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                            {c.contact_phone || c.contact_email || ''}
                          </div>
                        </>
                      ) : (
                        <span style={{ color: 'var(--ink-3)' }}>—</span>
                      )}
                    </td>
                    <td>
                      <Owner name={c.sales_owner_name} initials={c.sales_owner_initials} seed={c.sales_owner_var} />
                    </td>
                    <td>
                      <Owner name={c.ae_owner_name} initials={c.ae_owner_initials} seed={c.ae_owner_var} />
                    </td>
                    <td className="num" style={{ textAlign: 'right' }}>
                      {c.open_deals}
                    </td>
                    <td className="num" style={{ textAlign: 'right' }}>
                      {thb(c.revenue_ytd_satang)}
                    </td>
                    <td style={{ color: 'var(--ink-3)' }}>{relTime(c.last_activity_at, lang)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.companies.length === 0 && (
            <Empty>
              {gaps
                ? 'No coverage gaps — every company here has both owners.'
                : 'Nothing here yet.'}
            </Empty>
          )}

          <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--ink-2)' }}>
            {data.total} companies
          </div>
        </div>
      )}
    </>
  );
}

/** An unassigned owner is a real state worth seeing, not a blank cell. */
const Owner = ({ name, initials, seed }) =>
  name ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Avatar initials={initials} seed={seed} size={20} />
      {name}
    </span>
  ) : (
    <span style={{ color: 'var(--warning)', fontWeight: 600, fontSize: 12 }}>Unassigned</span>
  );
